import type { TextBlockModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import FontModifierComponent from "@/features/react-editor/components/modifiers/base/FontModifier";
import SavedStyleSelector from "@/features/react-editor/components/modifiers/base/SavedStyleSelector";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import twMerge from "@replo/design-system/utils/twMerge";
import { LucideALargeSmall, X } from "lucide-react";

interface FontStyleControlsProps {
  onChange: (modifiers: TextBlockModifierValue[]) => void;
  savedStyleMode: boolean;
  setSavedStyleMode: (mode: boolean) => void;
}

const FontStyleControls: React.FC<FontStyleControlsProps> = ({
  onChange,
  savedStyleMode,
  setSavedStyleMode,
}) => {
  const handleStyleChange = (modifiers: TextBlockModifierValue[]) => {
    onChange(modifiers);
    setSavedStyleMode(true);
  };

  return (
    <div className="flex w-full gap-2">
      <div className={twMerge(savedStyleMode && "w-full")}>
        <SavedStyleSelector
          onChange={handleStyleChange}
          onExitSavedStyleMode={() => setSavedStyleMode(false)}
          styleSelectorTrigger={({
            typography,
            displayName,
            onExitSavedStyleMode: exitSavedStyleMode,
          }) => {
            if (!savedStyleMode) {
              return (
                <IconButton
                  variant="secondary"
                  size="sm"
                  icon={<LucideALargeSmall size={12} />}
                  tooltipText="Use saved styles"
                />
              );
            }
            return (
              <Combobox.SelectionButton
                layoutClassName="w-full"
                title={displayName ?? "Select style"}
                startEnhancer={<LucideALargeSmall size={12} />}
                isPlaceholder={!typography.length}
                isDisabled={!typography.length}
                size="sm"
                titleAlignment="start"
                endEnhancer={
                  <X
                    size={12}
                    className="text-muted"
                    onClick={(e) => {
                      e.stopPropagation();
                      exitSavedStyleMode();
                    }}
                  />
                }
              />
            );
          }}
        />
      </div>
      {!savedStyleMode && (
        <div className="flex-1">
          <FontModifierComponent onChange={onChange} />
        </div>
      )}
    </div>
  );
};

export default FontStyleControls;
